import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

const KEY = "sonora_consent";
export const openCookieSettings = () => window.dispatchEvent(new Event("sonora:cookies"));

export const CookieBanner = () => {
  const [show, setShow] = useState(!localStorage.getItem(KEY));
  const [custom, setCustom] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, storage: true });
  useEffect(() => {
    const h = () => { setShow(true); setCustom(true); };
    window.addEventListener("sonora:cookies", h);
    return () => window.removeEventListener("sonora:cookies", h);
  }, []);
  if (!show) return null;
  const save = (p) => {
    localStorage.setItem(KEY, JSON.stringify({ ...p, necessary: true, at: new Date().toISOString() }));
    api.post("/privacy/consent", p).catch(() => {});
    setShow(false);
  };
  return (
    <div data-testid="cookie-banner" className="fixed left-4 right-4 sm:right-auto bottom-4 sm:bottom-28 z-[60] sm:max-w-md glass rounded-2xl p-5 reveal shadow-2xl">
      <div className="flex items-center gap-2 font-display text-lg font-bold"><Cookie size={18} className="text-[var(--hub)]" /> Your privacy, your call</div>
      <p className="text-sm text-zinc-400 mt-2">We use strictly necessary cookies to run Soundra. With your consent we also use analytics and persistent storage (offline queue, preferences). <Link to="/privacy-policy" className="underline">Learn more</Link></p>
      {custom && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="flex justify-between items-center"><span>Strictly necessary</span><Switch checked disabled /></div>
          <div className="flex justify-between items-center"><span>Analytics</span><Switch data-testid="cookie-analytics-switch" checked={prefs.analytics} onCheckedChange={(v) => setPrefs({ ...prefs, analytics: v })} /></div>
          <div className="flex justify-between items-center"><span>Persistent storage</span><Switch data-testid="cookie-storage-switch" checked={prefs.storage} onCheckedChange={(v) => setPrefs({ ...prefs, storage: v })} /></div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        <button data-testid="cookie-accept-all" onClick={() => save({ analytics: true, storage: true })} className="px-4 h-9 rounded-full bg-white text-black text-sm font-semibold">Accept all</button>
        <button data-testid="cookie-reject" onClick={() => save({ analytics: false, storage: false })} className="px-4 h-9 rounded-full border border-white/15 text-sm">Reject optional</button>
        {custom ? (
          <button data-testid="cookie-save" onClick={() => save(prefs)} className="px-4 h-9 rounded-full border border-white/15 text-sm">Save choices</button>
        ) : (
          <button data-testid="cookie-customize" onClick={() => setCustom(true)} className="px-4 h-9 text-sm text-zinc-400 hover:text-white">Customize</button>
        )}
      </div>
    </div>
  );
};

export const Footer = () => {
  const t = useT();
  return (
    <footer data-testid="legal-footer" className="border-t border-white/10 mt-24">
      <div className="max-w-[1600px] mx-auto px-6 py-12 grid gap-8 md:grid-cols-4 text-sm">
        <div className="md:col-span-2">
          <div className="font-display text-3xl font-black">soundra</div>
          <p className="text-zinc-500 mt-2 max-w-md">The home of independent music. Soundra Music SAS acts as a hosting provider (hébergeur) under LCEN art. 6 — content is published by artists.</p>
          <p className="text-zinc-500 mt-2">Support: soundra.help@outlook.com · All prices include applicable VAT/TVA.</p>
        </div>
        <div className="flex flex-col gap-2">
          <span className="eyebrow mb-1">Legal</span>
          <Link to="/terms-of-service" data-testid="footer-terms" className="text-zinc-400 hover:text-white">{t("terms")}</Link>
          <Link to="/privacy-policy" data-testid="footer-privacy" className="text-zinc-400 hover:text-white">{t("privacyPolicy")}</Link>
          <Link to="/legal-notices" data-testid="footer-legal" className="text-zinc-400 hover:text-white">{t("legal")}</Link>
          <button data-testid="footer-cookies" onClick={openCookieSettings} className="text-left text-zinc-400 hover:text-white">Cookie settings</button>
        </div>
        <div className="flex flex-col gap-2">
          <span className="eyebrow mb-1">Soundra</span>
          <Link to="/charts" className="text-zinc-400 hover:text-white">{t("charts")}</Link>
          <Link to="/pricing" className="text-zinc-400 hover:text-white">{t("pricing")}</Link>
          <Link to="/settings/privacy" className="text-zinc-400 hover:text-white">{t("privacy")}</Link>
        </div>
      </div>
      <div className="text-center text-xs font-mono text-zinc-600 pb-8">© 2026 Soundra Music SAS · Paris</div>
    </footer>
  );
};
